import { Button } from "@/components/ui/button";
import { X } from "@/components/logos/X";
import { clsx } from "clsx";
import {
  type GeneratedContent,
  initiateTwitterLogin,
  postTweet,
} from "@/api/twitter";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoadingButton } from "@/components/LoadingButton";
import { TweetImage } from "@/components/content-creation/TweetImage";
import { useMutation } from "@tanstack/react-query";
import type { Persona } from "@/api/personas";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Badge } from "../ui/badge";
import { Link } from "react-router-dom";

export function PostTweetButton({
  className,
  persona,
  tweet,
}: {
  className?: string;
  persona: Pick<Persona, "persona_id" | "twitter">;
  tweet: GeneratedContent;
}) {
  const [open, setOpen] = useState(false);

  return tweet.posted ? (
    <Link
      to={`https://x.com/${persona.twitter}/status/${tweet.twitter_post_id}`}
      target="_blank"
    >
      <Badge
        variant="outline"
        className="gap-1 hover:border-1 hover:border-accent"
      >
        <X width={12} height={12} fill="white" />
        <span className="text-xs">Posted</span>
      </Badge>
    </Link>
  ) : (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={clsx(
            "flex items-center gap-1 rounded-full flex-1 sm:flex-none bg-black text-white hover:bg-zinc-800 hover:text-white border-black",
            className,
          )}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <X width={12} height={12} fill="white" />
          </div>
          <span className="sm:inline">Post</span>
        </Button>
      </AlertDialogTrigger>
      {persona.twitter ? (
        <TweetConfirmationAlertDialogContent
          tweet={tweet}
          onClose={() => setOpen(false)}
        />
      ) : (
        <ConnectTwitterAlertDialogContent
          personaId={persona.persona_id}
          onClose={() => setOpen(false)}
        />
      )}
    </AlertDialog>
  );
}

function TweetConfirmationAlertDialogContent({
  tweet,
  onClose,
}: {
  tweet: GeneratedContent;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const postTweetMutation = useMutation({
    mutationFn: postTweet,
    onSuccess: () => {
      toast({ description: "Tweet posted successfully." });
      onClose();
    },
    onError: () => {
      toast({ description: "Could not post tweet. Please try again later." });
    },
  });

  return (
    <AlertDialogContent
      className="max-h-screen overflow-y-auto"
      /* Do not close alert on escape key press */
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <AlertDialogHeader>
        <AlertDialogTitle>Post Tweet</AlertDialogTitle>
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground">
            Are you sure you want to post the tweet below?
          </p>
          <p>
            {tweet.text} {tweet.emoji}
          </p>

          <div className="grid grid-cols-2 gap-2 self-center mb-2">
            {tweet.images.map((imageId) => (
              <TweetImage
                className="h-auto min-h-16"
                key={imageId}
                id={imageId}
              />
            ))}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <LoadingButton
            loading={postTweetMutation.isPending}
            onClick={() => postTweetMutation.mutate(tweet.doc_id)}
          >
            Post
          </LoadingButton>
        </AlertDialogFooter>
      </AlertDialogHeader>
    </AlertDialogContent>
  );
}

function ConnectTwitterAlertDialogContent({
  personaId,
  onClose,
}: {
  personaId: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const connectTwitterMutation = useMutation({
    mutationFn: () => {
      return initiateTwitterLogin(
        personaId,
        encodeURIComponent(window.location.href),
      );
    },
    onSuccess: (redirectUrl) => {
      window.location.href = redirectUrl;
      toast({ description: "Twitter connected successfully." });
      onClose();
    },
    onError: () => {
      toast({
        description: "Could not connect to Twitter. Please try again later.",
      });
    },
  });

  return (
    <AlertDialogContent
      className="max-h-screen overflow-y-auto"
      /* Do not close alert on escape key press */
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <AlertDialogHeader>
        <AlertDialogTitle>Twitter Not Connected</AlertDialogTitle>
        <p>You need to connect your Twitter account to post tweets.</p>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <LoadingButton
            loading={connectTwitterMutation.isPending}
            onClick={() => connectTwitterMutation.mutate()}
          >
            Connect Twitter
          </LoadingButton>
        </AlertDialogFooter>
      </AlertDialogHeader>
    </AlertDialogContent>
  );
}
